import React, { CSSProperties, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Button, Flex, Form, FormInstance, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import useUnsavedChangesBlocker from '../../shared/hooks/useUnsavedChangesBlocker'
import { usePageContext } from './PageContext'
import { CONTENT_WIDTH } from './pageStyles'

interface PageSaveBarProps<Values extends object> {
  form: FormInstance<Values>
  /**
   * Width of the form the bar belongs to; the bar's content is limited to it. Defaults to the page's form
   * column. `none` for full-width forms.
   */
  width?: CSSProperties['maxWidth']
  /** Saved values the form is compared against. The bar shows while the form differs from them. */
  initialValues?: Partial<Values>
  saving?: boolean
  /** Prevents saving, e.g. in a locked scenario. Tell the user why somewhere on the page. */
  disabled?: boolean
  /** Defaults to submitting the form, so validation and the form's onFinish run as before. */
  onSave?: () => void
}

const isSame = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * Save bar at the bottom of the surrounding `Page`, shown while the form has unsaved changes. Replaces the
 * form's own save button. Leaving the page with unsaved changes asks for a confirmation.
 */
const PageSaveBar = <Values extends object>({
  form,
  width: widthProp,
  initialValues,
  saving,
  disabled,
  onSave
}: PageSaveBarProps<Values>) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const pageContext = usePageContext()
  const footerNode = pageContext?.footerNode
  const width = widthProp ?? pageContext?.contentWidth ?? CONTENT_WIDTH
  const values = Form.useWatch(watched => watched, form) as Partial<Values> | undefined

  const changedCount = useMemo(() => {
    if (!values) return 0
    return Object.keys(values).filter(key => !isSame(values[key as keyof Values], initialValues?.[key as keyof Values]))
      .length
  }, [values, initialValues])

  const reset = () => form.setFieldsValue(initialValues as Parameters<typeof form.setFieldsValue>[0])

  useUnsavedChangesBlocker({
    hasUnsavedChanges: () => changedCount > 0,
    onDiscard: reset,
    title: t('global:unsaved-changes-title', 'Tallentamattomia muutoksia'),
    message: t('global:unsaved-changes-leave', 'Poistutaanko sivulta tallentamatta muutoksia?')
  })

  if (!footerNode || changedCount === 0) return null

  return createPortal(
    <div
      style={{
        padding: `${token.paddingSM}px ${token.paddingLG}px`,
        borderTop: `1px solid ${token.colorSplit}`,
        background: token.colorPrimaryBg
      }}
    >
      {/* Content uses the centered form column, so the buttons sit below the form's right edge */}
      <Flex align="center" gap={token.marginSM} style={{ maxWidth: width, margin: '0 auto' }}>
        <Typography.Text>
          {t('global:unsaved-changes', { count: changedCount, defaultValue: '{{count}} tallentamatonta muutosta' })}
        </Typography.Text>
        <Flex gap={token.marginXS} style={{ marginLeft: 'auto' }}>
          <Button onClick={reset}>{t('global:cancel')}</Button>
          <Button type="primary" loading={saving} disabled={disabled} onClick={onSave ?? (() => form.submit())}>
            {t('global:save')}
          </Button>
        </Flex>
      </Flex>
    </div>,
    footerNode
  )
}

export default PageSaveBar
